import { ColumnDef } from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ColumnOption, ColumnSelectorProps, ColumnPreferences } from '../types';

export const useColumnSelector = <T = any>({
  columns,
  fixedColumns = [],
  selected,
  storageKey,
}: Pick<
  ColumnSelectorProps<T>,
  'columns' | 'fixedColumns' | 'selected' | 'storageKey'
>) => {
  // Extraer ID de una columna
  const getColumnId = useCallback((column: ColumnDef<T>): string => {
    // Preferir 'id' explícito si existe; si no, usar 'accessorKey'
    const anyCol = column as any;
    return anyCol.id || anyCol.accessorKey || 'unknown';
  }, []);
  // Extraer label de una columna
  const getColumnLabel = useCallback(
    (column: ColumnDef<T>): string => {
      const header = column.header;
      if (typeof header === 'string') return header;
      if (typeof header === 'function') return 'Columna'; // Fallback
      return getColumnId(column);
    },
    [getColumnId]
  );
  // Convertir columnas a opciones
  const availableColumns = useMemo<ColumnOption[]>(() => {
    return columns.map((column) => {
      const id = getColumnId(column);
      return {
        id,
        label: getColumnLabel(column),
        isFixed: fixedColumns.includes(id),
        isVisible: true,
      };
    });
  }, [columns, fixedColumns, getColumnId, getColumnLabel]);

  // 🎯 Funciones para persistencia en localStorage
  const saveColumnPreferences = useCallback(
    (selectedColumns: string[]) => {
      if (!storageKey) return;

      const preferences: ColumnPreferences = {
        selectedColumns,
        timestamp: Date.now(),
      };

      try {
        localStorage.setItem(
          `columnSelector_${storageKey}`,
          JSON.stringify(preferences)
        );
      } catch (error) {
        console.warn('Error saving column preferences:', error);
      }
    },
    [storageKey]
  );

  const loadColumnPreferences = useCallback((): string[] | null => {
    if (!storageKey) return null;

    try {
      const stored = localStorage.getItem(`columnSelector_${storageKey}`);
      if (!stored) return null;

      const preferences: ColumnPreferences = JSON.parse(stored);

      // Verificar que las columnas guardadas aún existen
      const validColumns = preferences.selectedColumns.filter((columnId) =>
        availableColumns.some((col) => col.id === columnId)
      );

      return validColumns.length > 0 ? validColumns : null;
    } catch (error) {
      console.warn('Error loading column preferences:', error);
      return null;
    }
  }, [storageKey, availableColumns]);

  // 🎯 Cargar preferencias al montar el componente
  useEffect(() => {
    if (storageKey && selected.length === 0) {
      const savedPreferences = loadColumnPreferences();
      if (savedPreferences && savedPreferences.length > 0) {
        // Notificar al componente padre para que actualice las columnas seleccionadas
        // Esto se maneja desde el componente padre
      }
    }
  }, [storageKey, selected.length, loadColumnPreferences]);
  // Estado del dropdown
  const [isOpen, setIsOpen] = useState(false);
  // Togglear selección de columna
  const toggleColumn = useCallback(
    (columnId: string) => {
      const column = availableColumns.find((col) => col.id === columnId);
      if (!column || column.isFixed) return;
      // El control de selección se hace desde el padre, así que aquí solo se expone la intención
    },
    [availableColumns, fixedColumns]
  );
  // Seleccionar todas las columnas
  const selectAll = useCallback(() => {
    // El control de selección se hace desde el padre
  }, [availableColumns]);
  // Restaurar por defecto
  const resetToDefault = useCallback(() => {
    // El control de selección se hace desde el padre
  }, [selectAll]);
  // Estadísticas
  const stats = useMemo(() => {
    const total = availableColumns.length;
    const selectedCount = selected?.length ?? 0;
    const nonFixedTotal = availableColumns.filter((col) => !col.isFixed).length;
    const nonFixedSelected =
      selected?.filter((id) => !fixedColumns.includes(id)).length ?? 0;
    return {
      total,
      selected: selectedCount,
      nonFixedTotal,
      nonFixedSelected,
      hasFilters: selectedCount < total,
    };
  }, [availableColumns, selected, fixedColumns]);
  return {
    // Estado
    isOpen,
    setIsOpen,
    selectedColumns: selected,
    availableColumns,
    // Acciones
    toggleColumn,
    selectAll,
    resetToDefault,
    // Persistencia
    saveColumnPreferences,
    loadColumnPreferences,
    // Helpers
    isColumnSelected: (columnId: string) => selected?.includes(columnId),
    isColumnFixed: (columnId: string) => fixedColumns.includes(columnId),
    canToggleColumn: (columnId: string) => {
      const isFixed = fixedColumns.includes(columnId);
      if (isFixed) return false;
      const isSelected = selected?.includes(columnId);
      if (!isSelected) return true;
      // Si está seleccionada, verificar si se puede deseleccionar
      const nonFixedSelected =
        selected?.filter((id) => !fixedColumns.includes(id)) ?? [];
      return nonFixedSelected.length > 1;
    },
    // Stats
    stats,
  };
};
